import { useState, useEffect } from "react";
import { isBlankHtml } from "utils/htmlContent";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";
import RichTextEditor from "components/RichTextEditor";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { adminApi } from "services/api";

// Below md the DataTable shows each row as a card, which would print long text in full; clamp it
// there (column `clampLines`, default 3) so cards stay scannable. Desktop cells are unchanged.
function ClampedCell({ value, column }) {
  return (
    <MDBox
      component="span"
      color="inherit"
      sx={({ breakpoints }) => ({
        [breakpoints.down("md")]: {
          display: "-webkit-box",
          WebkitLineClamp: column.clampLines || 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        },
      })}
    >
      {value}
    </MDBox>
  );
}

ClampedCell.propTypes = {
  value: PropTypes.node,
  column: PropTypes.shape({ clampLines: PropTypes.number }).isRequired,
};

// Full-screen dialog (below sm): let the form content scroll between a pinned title and action bar.
const fullScreenForm = { display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 };

function Pages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Save failures show inside the open dialog, not on the page behind it.
  const [dialogError, setDialogError] = useState("");
  const [success, setSuccess] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    content: "",
    metaDescription: "",
    metaKeywords: "",
    published: true,
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getPages();
      setPages(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to fetch pages");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setEditMode(false);
    setFormData({
      title: "",
      slug: "",
      content: "",
      metaDescription: "",
      metaKeywords: "",
      published: true,
    });
    setDialogError("");
    setOpenDialog(true);
  };

  const handleEdit = (page) => {
    setEditMode(true);
    setCurrentId(page._id);
    setFormData({
      title: page.title,
      slug: page.slug,
      content: page.content || "",
      metaDescription: page.metaDescription || "",
      metaKeywords: page.metaKeywords || "",
      published: page.published !== undefined ? page.published : true,
    });
    setDialogError("");
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setCurrentId(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    if (name === "title" && !editMode) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDialogError("");
    setError("");
    setSuccess("");

    if (isBlankHtml(formData.content)) {
      setDialogError("Content is required");
      return;
    }

    try {
      if (editMode) {
        await adminApi.updatePage(currentId, formData);
        setSuccess("Page updated successfully");
      } else {
        await adminApi.createPage(formData);
        setSuccess("Page created successfully");
      }
      handleCloseDialog();
      fetchPages();
    } catch (err) {
      setDialogError(
        err.response?.data?.error || err.response?.data?.message || "Operation failed"
      );
    }
  };

  const handleDelete = async () => {
    if (!selectedPage) return;

    try {
      await adminApi.deletePage(selectedPage._id);
      setSuccess("Page deleted successfully");
      fetchPages();
      handleMenuClose();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to delete page");
    }
  };

  const handleMenuOpen = (event, page) => {
    setAnchorEl(event.currentTarget);
    setSelectedPage(page);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPage(null);
  };

  const columns = [
    { Header: "title", accessor: "title", width: "25%", align: "left", Cell: ClampedCell },
    {
      Header: "slug",
      accessor: "slug",
      width: "25%",
      align: "left",
      Cell: ClampedCell,
      clampLines: 2,
    },
    { Header: "status", accessor: "status", width: "15%", align: "center" },
    { Header: "updated", accessor: "updated", width: "20%", align: "left" },
    { Header: "actions", accessor: "actions", width: "15%", align: "center" },
  ];

  const rows = pages.map((page) => ({
    title: page.title,
    slug: page.slug,
    status: (
      <MDBox ml={-1}>
        <span
          style={{
            padding: "4px 12px",
            borderRadius: "4px",
            fontSize: "0.75rem",
            fontWeight: "bold",
            color: "white",
            backgroundColor: page.published ? "#4caf50" : "#f44336",
          }}
        >
          {page.published ? "Published" : "Draft"}
        </span>
      </MDBox>
    ),
    updated: new Date(page.updatedAt).toLocaleDateString(),
    actions: (
      <IconButton onClick={(e) => handleMenuOpen(e, page)}>
        <Icon>more_vert</Icon>
      </IconButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexWrap="wrap"
                gap={1.5}
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Website Pages
                </MDTypography>
                <MDButton variant="contained" color="white" onClick={handleOpenDialog}>
                  Add Page
                </MDButton>
              </MDBox>

              {error && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="error" dismissible onClose={() => setError("")}>
                    {error}
                  </MDAlert>
                </MDBox>
              )}

              {success && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="success" dismissible onClose={() => setSuccess("")}>
                    {success}
                  </MDAlert>
                </MDBox>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2">Loading...</MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>{editMode ? "Edit Page" : "Add New Page"}</DialogTitle>
        <form onSubmit={handleSubmit} style={fullScreenDialog ? fullScreenForm : undefined}>
          <DialogContent>
            {dialogError && (
              <MDBox mb={2}>
                <MDAlert color="error" dismissible onClose={() => setDialogError("")}>
                  {dialogError}
                </MDAlert>
              </MDBox>
            )}
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <RichTextEditor
                label="Content"
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                helperText="Use the toolbar to format text, insert images, embed videos, and more"
                height={400}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Meta Description"
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Meta Keywords (comma-separated)"
                name="metaKeywords"
                value={formData.metaKeywords}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.published}
                    onChange={handleChange}
                    name="published"
                    color="primary"
                  />
                }
                label="Published"
              />
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCloseDialog} color="secondary">
              Cancel
            </MDButton>
            <MDButton type="submit" color="info">
              {editMode ? "Update" : "Create"}
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleEdit(selectedPage)}>Edit</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      <Footer />
    </DashboardLayout>
  );
}

export default Pages;
